import React, { useState } from 'react';
import { 
  Card, 
  CardBody, 
  Container, 
  PageContainer, 
  PageHeader, 
  PageTitle, 
  FormSelect, 
  FormGroup, 
  FormInput, 
  Grid, 
  GridItem, 
  StatusBadge, 
  Tag, 
  TagGroup 
} from '../../../styles/components';
import styled, { keyframes } from 'styled-components';

// Futuristic animation keyframes
const glowPulse = keyframes`
  0% { box-shadow: 0 0 10px rgba(99, 102, 241, 0.3), inset 0 0 5px rgba(99, 102, 241, 0.1); }
  50% { box-shadow: 0 0 15px rgba(99, 102, 241, 0.5), inset 0 0 8px rgba(99, 102, 241, 0.2); }
  100% { box-shadow: 0 0 10px rgba(99, 102, 241, 0.3), inset 0 0 5px rgba(99, 102, 241, 0.1); }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const floatAnimation = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0); }
`;

// Enhanced Page Container with space background
const SpacePageContainer = styled(PageContainer)`
  background: transparent;
  position: relative;
  isolation: isolate;
  min-height: 100vh;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: -1;
    opacity: 0.15;
    background-image: 
      radial-gradient(2px 2px at 40px 60px, rgba(255, 255, 255, 0.9), rgba(0, 0, 0, 0)),
      radial-gradient(2px 2px at 100px 150px, rgba(255, 255, 255, 0.9), rgba(0, 0, 0, 0)),
      radial-gradient(1px 1px at 200px 80px, rgba(255, 255, 255, 0.8), rgba(0, 0, 0, 0)),
      radial-gradient(2px 2px at 300px 250px, rgba(255, 255, 255, 0.9), rgba(0, 0, 0, 0)),
      radial-gradient(1px 1px at 400px 100px, rgba(255, 255, 255, 0.8), rgba(0, 0, 0, 0)),
      radial-gradient(1px 1px at 500px 200px, rgba(255, 255, 255, 0.7), rgba(0, 0, 0, 0)),
      radial-gradient(1px 1px at 50px 300px, rgba(255, 255, 255, 0.8), rgba(0, 0, 0, 0)),
      radial-gradient(1px 1px at 250px 350px, rgba(255, 255, 255, 0.7), rgba(0, 0, 0, 0)),
      radial-gradient(1px 1px at 550px 350px, rgba(255, 255, 255, 0.8), rgba(0, 0, 0, 0));
  }
`;

// Space-themed container
const GlassContainer = styled(Container)`
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  background: rgba(15, 23, 42, 0.25);
  border-radius: 16px;
  border: 1px solid rgba(99, 102, 241, 0.15);
  padding: 1.5rem;
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.2);
`;

// Glowing page title
const GlowingPageTitle = styled(PageTitle)`
  color: #fff;
  text-shadow: 0 0 10px rgba(99, 102, 241, 0.7);
  position: relative;
  margin-bottom: 1.5rem;
  font-weight: 700;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 100px;
    height: 3px;
    background: linear-gradient(90deg, rgba(99, 102, 241, 0.8), rgba(124, 58, 237, 0.8));
    border-radius: 3px;
    animation: ${glowPulse} 3s ease-in-out infinite;
  }
`;

// Styled components specific to this component
const StudentCard = styled(Card)`
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(99, 102, 241, 0.2);
  overflow: hidden;
  position: relative;
  
  &:hover {
    transform: translateY(-4px) scale(1.01);
    box-shadow: 0 8px 25px rgba(99, 102, 241, 0.25);
    border: 1px solid rgba(99, 102, 241, 0.4);
    
    &:before {
      opacity: 1;
    }
  }
  
  /* Futuristic highlight effect */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      120deg,
      transparent, 
      transparent,
      rgba(99, 102, 241, 0.05),
      transparent,
      transparent
    );
    background-size: 200% 100%;
    opacity: 0;
    transition: opacity 0.3s ease;
    animation: ${shimmer} 2s infinite linear;
    pointer-events: none;
  }
`;

const StudentAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(79, 70, 229, 0.2) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #fff;
  margin-right: 1.25rem;
  overflow: hidden;
  border: 2px solid rgba(99, 102, 241, 0.3);
  box-shadow: 0 0 15px rgba(99, 102, 241, 0.3);
  transition: all 0.3s ease;
  animation: ${floatAnimation} 3s infinite ease-in-out;
  
  ${StudentCard}:hover & {
    box-shadow: 0 0 20px rgba(99, 102, 241, 0.5);
    transform: scale(1.05);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
    
    ${StudentCard}:hover & {
      transform: scale(1.08);
    }
  }
`;

const StudentInfo = styled.div`
  flex: 1;
  color: #fff;
`;

const StudentName = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 0 0 8px rgba(99, 102, 241, 0.4);
  transition: color 0.3s ease;
  
  ${StudentCard}:hover & {
    color: #fff;
    text-shadow: 0 0 12px rgba(99, 102, 241, 0.6);
  }
`;

const StudentId = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  transition: color 0.3s ease;
  
  ${StudentCard}:hover & {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  background: rgba(15, 23, 42, 0.4);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid rgba(99, 102, 241, 0.15);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchInput = styled(FormInput)`
  max-width: 300px;
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid rgba(99, 102, 241, 0.3);
  color: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    background: rgba(15, 23, 42, 0.6);
    border: 1px solid rgba(99, 102, 241, 0.6);
    box-shadow: 0 0 15px rgba(99, 102, 241, 0.3);
    outline: none;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const StatusFilter = styled(FormSelect)`
  max-width: 200px;
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid rgba(99, 102, 241, 0.3);
  color: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    background: rgba(15, 23, 42, 0.6);
    border: 1px solid rgba(99, 102, 241, 0.6);
    box-shadow: 0 0 15px rgba(99, 102, 241, 0.3);
    outline: none;
  }
  
  option {
    background: rgba(15, 23, 42, 0.95);
    color: rgba(255, 255, 255, 0.9);
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

// Enhanced TagGroup component
const SpaceTagGroup = styled(TagGroup)`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

// Enhanced Tag component
const SpaceTag = styled(Tag)`
  background: rgba(99, 102, 241, 0.15);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 6px;
  padding: 0.3rem 0.6rem;
  font-size: 0.8rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.25);
    box-shadow: 0 0 10px rgba(99, 102, 241, 0.3);
  }
`;

// Enhanced Status Badge
const SpaceStatusBadge = styled(StatusBadge)`
  padding: 0.35rem 0.8rem;
  border-radius: 6px;
  font-weight: 600;
  text-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  ${StudentCard}:hover & {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
  }
`;

const StudentsList = ({ onViewProfile }) => {
  // Dummy data for students with real images
  const dummyStudents = [
    {
      id: 'S-2025-001',
      name: 'Ahmed Mohamed',
      email: 'ahmed.mohamed@student.guc.edu.eg',
      major: 'Computer Science',
      semester: 6,
      gpa: 3.75,
      internshipStatus: 'current',
      skills: ['JavaScript', 'React', 'Node.js'],
      avatar: 'AM',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80'
    },
    {
      id: 'S-2025-002',
      name: 'Sara Ahmed',
      email: 'sara.ahmed@student.guc.edu.eg',
      major: 'Business Informatics',
      semester: 8,
      gpa: 3.9,
      internshipStatus: 'completed',
      skills: ['Data Analysis', 'SQL', 'Python'],
      avatar: 'SA',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80'
    },
    {
      id: 'S-2025-003',
      name: 'Omar Ibrahim',
      email: 'omar.ibrahim@student.guc.edu.eg',
      major: 'Engineering',
      semester: 4,
      gpa: 3.2,
      internshipStatus: 'pending',
      skills: ['AutoCAD', 'MATLAB', 'C++'],
      avatar: 'OI',
      image: 'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80'
    },
    {
      id: 'S-2025-004',
      name: 'Nour Mahmoud',
      email: 'nour.mahmoud@student.guc.edu.eg',
      major: 'Computer Science',
      semester: 7,
      gpa: 3.6,
      internshipStatus: 'none',
      skills: ['Java', 'Spring', 'Databases'],
      avatar: 'NM',
      image: 'https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80'
    },
    {
      id: 'S-2025-005',
      name: 'Youssef Ali',
      email: 'youssef.ali@student.guc.edu.eg',
      major: 'Engineering',
      semester: 9,
      gpa: 3.4,
      internshipStatus: 'completed',
      skills: ['Mechanical Design', 'SolidWorks', 'Project Management'],
      avatar: 'YA',
      image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80'
    },
    {
      id: 'S-2025-006',
      name: 'Mariam Hassan',
      email: 'mariam.hassan@student.guc.edu.eg',
      major: 'Business Informatics',
      semester: 5,
      gpa: 3.8,
      internshipStatus: 'current',
      skills: ['Marketing', 'Data Science', 'R'],
      avatar: 'MH',
      image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80'
    }
  ];

  // State for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Filter students based on search term and status filter
  const filteredStudents = dummyStudents.filter(student => {
    const matchesSearch = student.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         student.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.major.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || student.internshipStatus === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Function to get status badge color
  const getStatusBadge = (status) => {
    switch(status) {
      case 'current':
        return <SpaceStatusBadge status="pending">Current Intern</SpaceStatusBadge>;
      case 'completed':
        return <SpaceStatusBadge status="accepted">Completed</SpaceStatusBadge>;
      case 'pending':
        return <SpaceStatusBadge status="interview">Application Pending</SpaceStatusBadge>;
      case 'none':
        return <SpaceStatusBadge status="closed">No Internship</SpaceStatusBadge>;
      default:
        return <SpaceStatusBadge>Unknown</SpaceStatusBadge>;
    }
  };

  return (
    <SpacePageContainer>
      <GlassContainer>
        <PageHeader>
          <GlowingPageTitle>Students Management</GlowingPageTitle>
        </PageHeader>
        
        <FilterBar>
          <FormGroup>
            <SearchInput 
              type="text" 
              placeholder="Search by name, ID, or major..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </FormGroup>
          
          <FormGroup>
            <StatusFilter
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="current">Current Interns</option>
              <option value="completed">Completed Internship</option>
              <option value="pending">Application Pending</option>
              <option value="none">No Internship</option>
            </StatusFilter>
          </FormGroup>
        </FilterBar>
        
        <Grid columns={1}>
          {filteredStudents.map(student => (
            <GridItem key={student.id}>
              <StudentCard onClick={() => onViewProfile(student.id)}>
                <CardBody>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <StudentAvatar>
                      {student.image ? (
                        <img src={student.image} alt={student.name} />
                      ) : (
                        student.avatar
                      )}
                    </StudentAvatar>
                    <StudentInfo>
                      <StudentName>{student.name}</StudentName>
                      <StudentId>{student.id} • {student.major}</StudentId>
                      <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.5rem', justifyContent: 'space-between' }}>
                        <div>
                          <SpaceTag color="primary">Semester {student.semester}</SpaceTag>
                          <SpaceTag color="secondary">GPA {student.gpa}</SpaceTag>
                        </div>
                        {getStatusBadge(student.internshipStatus)}
                      </div>
                    </StudentInfo>
                  </div>
                  <div style={{ marginTop: '1rem' }}>
                    <SpaceTagGroup>
                      {student.skills.map((skill, index) => (
                        <SpaceTag key={index}>{skill}</SpaceTag>
                      ))}
                    </SpaceTagGroup>
                  </div>
                </CardBody>
              </StudentCard>
            </GridItem>
          ))}
        </Grid>
        
        {filteredStudents.length === 0 && (
          <StudentCard>
            <CardBody>
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <h3 style={{ color: 'rgba(255, 255, 255, 0.9)', textShadow: '0 0 8px rgba(99, 102, 241, 0.4)' }}>No students found</h3>
                <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Try adjusting your search filters</p>
              </div>
            </CardBody>
          </StudentCard>
        )}
      </GlassContainer>
    </SpacePageContainer>
  );
};

export default StudentsList;
